import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { useToast } from '../../context/ToastContext';
import {
  getMatcherContext,
  runMatcher,
  getMatcherRun,
  selectCompetitions,
  getSwapOptions,
  requestSwap,
  getMySwapRequests,
} from '../../services/compMatcherService';
import styles from './StudentFindCompetitions.module.css';

/**
 * Find Competitions — the student-facing matcher.
 *
 * Slice 6 of COMP_MATCHER_MIGRATION_PLAN.md. The flow is fixed and short:
 *
 *   location  ->  confirm the project  ->  running  ->  awaiting review  ->  results
 *
 * Two things are deliberately not offered. The student does not choose or edit
 * the project — their programme contact assigned it, and they confirm it with a
 * single button. And the location is asked on every run rather than remembered,
 * because it decides which region's competition list the match is made against,
 * and a student who has moved would otherwise keep getting the wrong one.
 */

const COUNTRIES = [
  { value: 'India', label: 'India', hint: 'The India competition list' },
  { value: 'US', label: 'United States', hint: 'The US competition list' },
  { value: 'Worldwide', label: 'Other country', hint: 'Competitions open to students from any country' },
];

const StudentFindCompetitions = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [country, setCountry] = useState('');
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null);
  const [selected, setSelected] = useState([]);
  const [activating, setActivating] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapFrom, setSwapFrom] = useState(null);
  const [swapOptions, setSwapOptions] = useState([]);
  const [swapSearch, setSwapSearch] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [swapBusy, setSwapBusy] = useState(false);
  const [myRequests, setMyRequests] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await getMatcherContext();
      setContext(data);
      // Prefill from the profile as a convenience, but the student still has to
      // confirm it — the answer sent is always the one on screen this run.
      if (COUNTRIES.some((option) => option.value === data.country)) setCountry(data.country);

      // Only an approved run carries a payload; anything else comes back
      // without one and the page shows the waiting state instead.
      if (data.latest_run) {
        const full = await getMatcherRun(data.latest_run.id);
        setRun(full);
      }
      try {
        setMyRequests((await getMySwapRequests()).requests || []);
      } catch {
        // A swap-request read failing must not blank the whole page — the
        // matches are the point, the request list is context.
      }
    } catch (error) {
      toast.error('Could not load the matcher: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    if (!country) {
      toast.error('Choose where you are studying first.');
      return;
    }
    setRunning(true);
    try {
      const result = await runMatcher(country);
      if (result.reused) {
        toast.info(result.warning);
      } else {
        toast.success('Your project has been matched — it is with the CreatED team for review.');
      }
      await load();
    } catch (error) {
      toast.error('Matching failed: ' + error.message);
    } finally {
      setRunning(false);
    }
  };

  // The swap list is fetched only when it is opened: it is a browse over the
  // whole region's current competitions, not something to pull on every render.
  const openSwap = async (fromItem) => {
    setSwapFrom(fromItem);
    setSwapOpen(true);
    setSwapReason('');
    try {
      setSwapOptions((await getSwapOptions(swapSearch)).competitions || []);
    } catch (error) {
      toast.error('Could not load competitions: ' + error.message);
    }
  };

  const submitSwap = async (toListing) => {
    setSwapBusy(true);
    try {
      await requestSwap({
        runId: run.id,
        toListingId: toListing.id,
        fromListingId: swapFrom?.competition?.id || null,
        reason: swapReason || null,
      });
      toast.success('Sent to the CreatED team for approval.');
      setSwapOpen(false);
      setSwapFrom(null);
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSwapBusy(false);
    }
  };

  const toggle = (listingId) => {
    setSelected((prev) =>
      prev.includes(listingId)
        ? prev.filter((id) => id !== listingId)
        : [...prev, listingId]
    );
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const result = await selectCompetitions(run.id, selected);
      // Not every activation builds a workspace — report what actually landed
      // rather than counting the requests. Claiming success for a competition
      // with no workspace is how "added" and "nothing there" coexisted.
      const added = (result.activated || []).filter(
        (a) => a.enrollment?.status === 'enrolled' || a.enrollment?.status === 'already_enrolled'
      ).length;
      const failed = (result.activated || []).length - added;
      if (added) {
        toast.success(`${added} competition(s) added to your workspace.`);
      }
      if (failed) {
        toast.info(
          `${failed} competition(s) could not be opened as a workspace yet — the CreatED team has been notified.`
        );
      }
      setSelected([]);
      await load();
    } catch (error) {
      toast.error('Could not confirm your choice: ' + error.message);
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  const project = context?.project;
  // The cap resolved from the student's programme (PPP/product -> 1,
  // RBP/research -> 2). null means uncapped, which is most students today, so
  // every bit of this UI has to disappear cleanly rather than read as "0 left".
  const allowance = context?.allowance || {};
  const cap = allowance.allowance ?? null;
  const alreadyChosen = allowance.used ?? 0;
  const remaining = cap === null ? null : Math.max(0, cap - alreadyChosen);
  const atCap = remaining !== null && selected.length >= remaining;
  const status = run?.status;
  const items = run?.payload?.items || [];
  // Once anything from this report is in the workspace, re-matching is over:
  // a fresh run would replace a report the student has already acted on.
  const hasSelection = items.some((item) => item.selected);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Find Competitions</h1>
        <p className={styles.subtitle}>
          We match your project against competitions that fit your stage, your
          region and your timeline.
        </p>
      </div>

      {cap !== null && (
        <div className={styles.allowanceBanner}>
          <strong>
            {allowance.programme ? `Your ${allowance.programme} programme` : 'Your programme'}
            {' '}includes {cap} competition{cap === 1 ? '' : 's'}.
          </strong>{' '}
          {remaining === 0
            ? `You have chosen ${alreadyChosen} — that is all of them.`
            : `You have chosen ${alreadyChosen}, so you can still pick ${remaining}.`}
        </div>
      )}

      {!project ? (
        // Reachable even though enabling requires a project: the project may
        // have been deleted since, which nulls the reference.
        <Card>
          <p className={styles.pending}>
            No project has been set for matching yet. Your programme contact will
            choose one.
          </p>
        </Card>
      ) : (
        <>
          <Card>
            <span className={styles.eyebrow}>Your project</span>
            <h2 className={styles.projectName}>{project.name || 'Untitled project'}</h2>
            {project.description && (
              <p className={styles.projectDescription}>{project.description}</p>
            )}

            {status !== 'PENDING_REVIEW' && !running && !hasSelection && (
              <>
                <fieldset className={styles.locationBlock}>
                  <legend className={styles.eyebrow}>Where are you studying?</legend>
                  <p className={styles.hint}>
                    This decides which competition list we match against.
                  </p>
                  <div className={styles.locationOptions}>
                    {COUNTRIES.map((option) => (
                      <label
                        key={option.value}
                        className={`${styles.locationOption} ${
                          country === option.value ? styles.locationSelected : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="country"
                          checked={country === option.value}
                          onChange={() => setCountry(option.value)}
                        />
                        <span className={styles.optionLabel}>{option.label}</span>
                        <span className={styles.optionHint}>{option.hint}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <Button
                  onClick={handleRun}
                  loading={running}
                  disabled={!country || running}
                >
                  {run ? 'Match again with this project' : 'Proceed with this project'}
                </Button>
              </>
            )}
          </Card>

          {running && (
            <Card>
              <h3 className={styles.stateHeading}>Matching your project</h3>
              <LoadingSpinner
                size="medium"
                message="This can take a moment — hang tight."
              />
            </Card>
          )}

          {status === 'PENDING_REVIEW' && (
            <Card>
              <h3 className={styles.stateHeading}>Your matches are being reviewed</h3>
              <p className={styles.pending}>
                The CreatED team checks every set of matches before you see them.
                We will email you as soon as they are ready.
              </p>
            </Card>
          )}

          {status === 'REJECTED' && (
            <Card>
              <h3 className={styles.stateHeading}>We are re-running your matches</h3>
              <p className={styles.pending}>
                The team asked for a better set. Check back shortly.
              </p>
            </Card>
          )}

          {myRequests.filter((r) => r.status === 'PENDING').length > 0 && (
            <Card>
              <h3 className={styles.stateHeading}>Change requests with the team</h3>
              {myRequests
                .filter((r) => r.status === 'PENDING')
                .map((r) => (
                  <p key={r.id} className={styles.pending}>
                    You asked for <strong>{r.to_listing?.name}</strong>
                    {r.from_listing ? ` instead of ${r.from_listing.name}` : ''} — waiting on the
                    CreatED team.
                  </p>
                ))}
            </Card>
          )}

          {swapOpen && (
            <Card>
              <div className={styles.resultsHeader}>
                <h3 className={styles.stateHeading}>
                  {swapFrom
                    ? `Replace ${swapFrom.competition?.name}`
                    : 'Ask for another competition'}
                </h3>
                <button className={styles.swapLink} onClick={() => setSwapOpen(false)}>
                  Cancel
                </button>
              </div>
              <p className={styles.hint}>
                Only competitions you are eligible for are listed. Your request goes to the
                CreatED team before anything changes.
              </p>
              <input
                className={styles.swapSearch}
                value={swapSearch}
                onChange={(e) => setSwapSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && openSwap(swapFrom)}
                placeholder="Search by name, then press Enter"
              />
              <textarea
                className={styles.swapReason}
                rows={2}
                value={swapReason}
                onChange={(e) => setSwapReason(e.target.value)}
                placeholder="Why would this one suit your project better? (optional)"
              />
              {swapOptions.length === 0 ? (
                <p className={styles.pending}>
                  Nothing else you are eligible for right now.
                </p>
              ) : (
                swapOptions.map((option) => (
                  <div key={option.id} className={styles.swapRow}>
                    <div>
                      <div className={styles.compName}>{option.name}</div>
                      <div className={styles.meta}>
                        {option.is_rolling
                          ? 'Rolling deadline'
                          : option.deadline_text || 'Deadline not stated'}
                      </div>
                    </div>
                    <Button
                      size="small"
                      variant="outline"
                      loading={swapBusy}
                      onClick={() => submitSwap(option)}
                    >
                      Request
                    </Button>
                  </div>
                ))
              )}
            </Card>
          )}

          {items.length > 0 && (
            <Card>
              <div className={styles.resultsHeader}>
                <h3 className={styles.stateHeading}>
                  {items.length} competition{items.length === 1 ? '' : 's'} for your project
                </h3>
                <div className={styles.pickSummary}>
                  {remaining !== null && (
                    <span className={styles.pickCount}>
                      {selected.length} of {remaining} chosen
                    </span>
                  )}
                  {selected.length > 0 && (
                    <Button onClick={handleActivate} loading={activating} size="small">
                      Add {selected.length} to my workspace
                    </Button>
                  )}
                </div>
              </div>

              {items.map((item) => {
                const c = item.competition || {};
                const id = c.id;
                return (
                  <div key={item.rank} className={styles.result}>
                    <div className={styles.resultHead}>
                      <div>
                        <span className={styles.rank}>{item.rank}</span>
                        <span className={styles.compName}>{c.name}</span>
                        {item.fit_bucket && (
                          <span className={styles.bucket}>{item.fit_bucket}</span>
                        )}
                      </div>
                      <div className={styles.itemActions}>
                        {item.selected ? (
                          <span className={styles.inWorkspace}>✓ In your workspace</span>
                        ) : (
                          id && (
                            <label
                              className={`${styles.pick} ${
                                atCap && !selected.includes(id) ? styles.pickDisabled : ''
                              }`}
                              title={
                                atCap && !selected.includes(id)
                                  ? `Your programme includes ${cap} competition${cap === 1 ? '' : 's'}`
                                  : undefined
                              }
                            >
                              <input
                                type="checkbox"
                                checked={selected.includes(id)}
                                // Blocked client-side for clarity; the real
                                // enforcement is in select_competitions.
                                disabled={atCap && !selected.includes(id)}
                                onChange={() => toggle(id)}
                              />
                              Choose
                            </label>
                          )
                        )}
                        <button className={styles.swapLink} onClick={() => openSwap(item)}>
                          Ask for a different one
                        </button>
                      </div>
                    </div>
                    <p className={styles.reason}>{item.reason}</p>
                    <div className={styles.meta}>
                      {c.is_rolling
                        ? 'Rolling deadline'
                        : c.deadline_text || 'Deadline not stated'}
                      {c.official_urls?.[0] && (
                        <>
                          {' · '}
                          <a href={c.official_urls[0]} target="_blank" rel="noreferrer">
                            Official site
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default StudentFindCompetitions;
